// src/components/banks/FormAddEditBank.jsx
import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import i18next from "i18next";

import useCreateBank from "../../hooks/banks/useCreateBank";
import useUpdateBank from "../../hooks/banks/useUpdateBank";
import useGetBank from "../../hooks/banks/useGetBank";
import { useUploadMedia } from "../../hooks/media/useUploadMedia";
import useSingleImageField from "../../hooks/media/useSingleImageField";

import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";

import useGetCurrenciesForSelect from "../../hooks/currencies/useGetCurrenciesForSelect";

const FormAddEditBank = ({ isEdit = false, onSubmitSuccess }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    // Hooks
    const { createBank, isPending: isPendingCreate } = useCreateBank();
    const { updateBank, isPending: isPendingUpdate } = useUpdateBank();
    const { bank, isLoading: isLoadingBank } = useGetBank(isEdit ? id : null);
    const { uploadMedia, isPending: isUploading } = useUploadMedia();

    // Form States
    const [bankNameAr, setBankNameAr] = useState("");
    const [bankNameEn, setBankNameEn] = useState("");
    const [companyNameAr, setCompanyNameAr] = useState("");
    const [companyNameEn, setCompanyNameEn] = useState("");
    const [accountTypeAr, setAccountTypeAr] = useState("");
    const [accountTypeEn, setAccountTypeEn] = useState("");
    const [accountNumber, setAccountNumber] = useState("");
    const [iban, setIban] = useState("");
    const [currencyId, setCurrencyId] = useState("");
    const [notesAr, setNotesAr] = useState("");
    const [notesEn, setNotesEn] = useState("");
    const [status, setStatus] = useState("active");
    const [errors, setErrors] = useState({});

    // Lookups
    const { options: currenciesOptions } = useGetCurrenciesForSelect();

    // Image Hook
    const img = useSingleImageField({
        initialImage: null,
        uploadMedia,
        isArabic,
    });

    useEffect(() => {
        if (isEdit && bank) {
            setBankNameAr(bank?.bank_name?.ar || "");
            setBankNameEn(bank?.bank_name?.en || "");
            setCompanyNameAr(bank?.company_name?.ar || "");
            setCompanyNameEn(bank?.company_name?.en || "");
            setAccountTypeAr(bank?.account_type?.ar || "");
            setAccountTypeEn(bank?.account_type?.en || "");
            setAccountNumber(bank?.account_number || "");
            setIban(bank?.iban || "");
            setCurrencyId(bank?.currency_id || "");
            setNotesAr(bank?.notes?.ar || "");
            setNotesEn(bank?.notes?.en || "");
            setStatus(bank?.is_active ? "active" : "disabled");

            // Sync Image
            if (bank?.image) {
                img.setFromServerImage(bank.image);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, bank]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});

        const payload = {
            bank_name: {
                ar: bankNameAr || bankNameEn,
                en: bankNameEn || bankNameAr,
            },
            company_name: {
                ar: companyNameAr || companyNameEn,
                en: companyNameEn || companyNameAr,
            },
            account_type: {
                ar: accountTypeAr || accountTypeEn,
                en: accountTypeEn || accountTypeAr,
            },
            account_number: accountNumber,
            iban: iban,
            currency_id: currencyId,
            notes: {
                ar: notesAr || null,
                en: notesEn || null,
            },
            is_active: status === "active",
            ...img.getImagePayload(),
        };

        if (isEdit) {
            updateBank(
                { id, req: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                    },
                    onError: (err) => setErrors(err?.response?.data?.errors || {}),
                }
            );
        } else {
            createBank(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                },
                onError: (err) => setErrors(err?.response?.data?.errors || {}),
            });
        }
    };

    if (isEdit && isLoadingBank) return <Typography color="primary" sx={{ p: 4 }}>Loading...</Typography>;

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <LanguageToggle activeLang={activeLang} onChange={setActiveLang} showLabel />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {isEdit ? (isArabic ? "تعديل بنك" : "Edit Bank") : (isArabic ? "إضافة بنك جديد" : "Add New Bank")}
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        {/* Logo Upload */}
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2, flexDirection: 'column-reverse', alignItems: 'center' }}>
                            <FormInput
                                type="file"
                                label={isArabic ? "صورة البنك" : "Bank Logo"}
                                name="logo"
                                value={img.imagePreview}
                                onChange={img.handleImageChange}
                                isUploading={isUploading}
                                errorMessage={img.imageError || errors.image?.[0]}
                                successMessage={img.imageName && !img.imageError ? (isArabic ? "تم رفع الصورة" : "Image uploaded") : ""}
                                width={120}
                                height={120}
                                borderRadius="8px"
                                required={false}
                                onDeleted={img.handleDeleted}
                                mediaId={img.imageId}
                            />
                        </Grid>

                        {/* Basic Info */}
                        {showArabic && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="اسم البنك (عربي)"
                                        placeholder="أدخل اسم البنك بالعربي"
                                        value={bankNameAr}
                                        onChange={(e) => setBankNameAr(e.target.value)}
                                        error={errors["bank_name.ar"]}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="اسم الشركة (عربي)"
                                        placeholder="أدخل اسم الشركة التابع لها החשבון"
                                        value={companyNameAr}
                                        onChange={(e) => setCompanyNameAr(e.target.value)}
                                        error={errors["company_name.ar"]}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="نوع الحساب (عربي)"
                                        placeholder="مثال: حساب جاري، حساب توفير"
                                        value={accountTypeAr}
                                        onChange={(e) => setAccountTypeAr(e.target.value)}
                                        error={errors["account_type.ar"]}
                                        required
                                    />
                                </Grid>
                            </>
                        )}
                        {showEnglish && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Bank Name (English)"
                                        placeholder="Enter bank name in English"
                                        value={bankNameEn}
                                        onChange={(e) => setBankNameEn(e.target.value)}
                                        error={errors["bank_name.en"]}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Company Name (English)"
                                        placeholder="Enter company name in English"
                                        value={companyNameEn}
                                        onChange={(e) => setCompanyNameEn(e.target.value)}
                                        error={errors["company_name.en"]}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Account Type (English)"
                                        placeholder="E.g., Current Account, Savings Account"
                                        value={accountTypeEn}
                                        onChange={(e) => setAccountTypeEn(e.target.value)}
                                        error={errors["account_type.en"]}
                                        required
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "رقم الحساب" : "Account Number"}
                                placeholder="1234567890"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value)}
                                error={errors.account_number}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="IBAN"
                                placeholder="SA0000000000000000000000"
                                value={iban}
                                onChange={(e) => setIban(e.target.value)}
                                error={errors.iban}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="select"
                                label={isArabic ? "العملة" : "Currency"}
                                value={currencyId}
                                options={currenciesOptions || []}
                                onChange={(e) => setCurrencyId(e.target.value)}
                                error={errors.currency_id}
                                required
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <FormInput
                                type="select"
                                label={isArabic ? "الحالة" : "Status"}
                                value={status}
                                options={[
                                    { value: "active", label: isArabic ? "نشط" : "Active" },
                                    { value: "disabled", label: isArabic ? "معطل" : "Disabled" },
                                ]}
                                onChange={(e) => setStatus(e.target.value)}
                                error={errors.is_active}
                                required={false}
                            />
                        </Grid>

                        {showArabic && (
                            <Grid item xs={12}>
                                <FormInput
                                    type="textarea"
                                    label="ملاحظات (عربي)"
                                    placeholder="أدخل أي ملاحظات إضافية"
                                    value={notesAr}
                                    onChange={(e) => setNotesAr(e.target.value)}
                                    error={errors["notes.ar"]}
                                    required={false}
                                />
                            </Grid>
                        )}
                        {showEnglish && (
                            <Grid item xs={12}>
                                <FormInput
                                    type="textarea"
                                    label="Notes (English)"
                                    placeholder="Enter any additional notes"
                                    value={notesEn}
                                    onChange={(e) => setNotesEn(e.target.value)}
                                    error={errors["notes.en"]}
                                    required={false}
                                />
                            </Grid>
                        )}

                    </Grid>

                    {/* Actions */}
                    <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/banks")}
                            sx={{ minWidth: 120, borderRadius: 2 }}
                        >
                            {isArabic ? "إلغاء" : "Cancel"}
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isPendingCreate || isPendingUpdate || isUploading}
                            sx={{ minWidth: 150, borderRadius: 2 }}
                        >
                            {(isPendingCreate || isPendingUpdate) ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                isEdit ? (isArabic ? "تعديل" : "Update") : (isArabic ? "إضافة" : "Add")
                            )}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default FormAddEditBank;
